// src/components/Apm/Profile/CompanyProfileDetails.js

import { useMemo, useState } from 'react';

import { Form, Formik, setNestedObjectValues } from 'formik';

import { toast } from 'react-toastify';

import { useTranslation } from 'react-i18next';

import i18n from 'i18next';
import * as Yup from 'yup';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useUpdateCurrentCompanyMutation } from '../../../redux/features/companyApi';

import { useGetCountriesQuery } from '../../../redux/features/countryApi';

import Autocomplete from '../../Controls/FormsUI/AutocompleteWrapper';

import ButtonAction from '../../Controls/Buttons/ButtonAction';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';

import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';

import { initialValuesCompany } from '../Tables/InitialValues/initialValuesCompany';

const validationSchema = Yup.object().shape({
  companyname: Yup.string()
    .max(150, () => i18n.t('yup.max_150_characters'))
    .required(() => i18n.t('yup.required')),

  fiscalcode: Yup.string()
    .max(20, () => i18n.t('yup.max_20_characters'))
    .nullable(),

  vatcode: Yup.string()
    .max(20, () => i18n.t('yup.max_20_characters'))
    .nullable(),

  regcom: Yup.string()
    .max(20, () => i18n.t('yup.max_20_characters'))
    .nullable(),

  countrycodelegal: Yup.object()
    .required(() => i18n.t('yup.required'))
    .typeError(() => i18n.t('yup.required')),

  zipcodelegal: Yup.string()
    .max(20, () => i18n.t('yup.max_20_characters'))
    .nullable(),

  citylegal: Yup.string()
    .max(50, () => i18n.t('yup.max_50_characters'))
    .nullable(),

  addresslegal: Yup.string()
    .max(120, () => i18n.t('yup.max_120_characters'))
    .nullable(),

  countrycodepost: Yup.object()
    .required(() => i18n.t('yup.required'))
    .typeError(() => i18n.t('yup.required')),

  zipcodepost: Yup.string()
    .max(20, () => i18n.t('yup.max_20_characters'))
    .nullable(),

  citypost: Yup.string()
    .max(50, () => i18n.t('yup.max_50_characters'))
    .nullable(),

  addresspost: Yup.string()
    .max(120, () => i18n.t('yup.max_120_characters'))
    .nullable(),

  email: Yup.string()
    .email(() => i18n.t('yup.invalid_email'))
    .nullable(),

  phone: Yup.string()
    .max(30, () => i18n.t('yup.max_30_characters'))
    .nullable(),

  skype: Yup.string()
    .max(30, () => i18n.t('yup.max_30_characters'))
    .nullable(),

  comment: Yup.string()
    .max(500, () => i18n.t('yup.max_500_characters'))
    .nullable(),
});

const normalizeNullableString = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();

  return normalized || null;
};

const buildCompanyPayload = (values) => ({
  companyname: values.companyname?.trim() ?? '',

  fiscalcode: normalizeNullableString(values.fiscalcode),

  vatcode: normalizeNullableString(values.vatcode),

  regcom: normalizeNullableString(values.regcom),

  countrycodelegal: values.countrycodelegal ?? null,

  zipcodelegal: normalizeNullableString(values.zipcodelegal),

  citylegal: normalizeNullableString(values.citylegal),

  addresslegal: normalizeNullableString(values.addresslegal),

  countrycodepost: values.countrycodepost ?? null,

  zipcodepost: normalizeNullableString(values.zipcodepost),

  citypost: normalizeNullableString(values.citypost),

  addresspost: normalizeNullableString(values.addresspost),

  email: normalizeNullableString(values.email),

  phone: normalizeNullableString(values.phone),

  skype: normalizeNullableString(values.skype),

  comment: normalizeNullableString(values.comment),
});

const getErrorStatus = (error) => error?.status ?? error?.originalStatus ?? error?.response?.status;

export const CompanyProfileDetails = ({ company, canEdit }) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [edit, setEdit] = useState(false);

  useGetCountriesQuery();

  const [updateCurrentCompany, { isLoading: isUpdatingCompany }] = useUpdateCurrentCompanyMutation();

  const formInitialValues = useMemo(
    () => ({
      ...initialValuesCompany,
      ...(company ?? {}),

      countrycodelegal: company?.countrycodelegal ?? initialValuesCompany.countrycodelegal ?? null,

      countrycodepost: company?.countrycodepost ?? initialValuesCompany.countrycodepost ?? null,
    }),
    [company]
  );

  const handleSubmit = async (values, { resetForm }) => {
    if (!canEdit) {
      toast.error(
        t('toast.only_account_manager_allowed', {
          defaultValue: 'You are not allowed to edit this company.',
        })
      );

      return;
    }

    try {
      const updatedContext = await updateCurrentCompany(buildCompanyPayload(values)).unwrap();

      resetForm({
        values: {
          ...initialValuesCompany,
          ...updatedContext.company,
        },
      });

      setEdit(false);

      toast.success(
        t('toast.company_profile_updated_successfully', {
          defaultValue: 'Company profile updated successfully.',
        })
      );
    } catch (error) {
      console.error('Company profile update failed:', error);

      if (getErrorStatus(error) === 403) {
        toast.error(
          t('toast.only_account_manager_allowed', {
            defaultValue: 'You are not allowed to edit this company.',
          })
        );

        return;
      }

      toast.error(
        t('toast.not_updated_try_again', {
          defaultValue: 'The company could not be updated. Please try again.',
        })
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ dirty, resetForm, setTouched, submitForm, validateForm }) => {
        const handleCancel = () => {
          resetForm({
            values: formInitialValues,
          });

          setEdit(false);
        };

        const handleSave = async () => {
          const errors = await validateForm();

          if (Object.keys(errors).length > 0) {
            setTouched(setNestedObjectValues(errors, true));

            toast.error(t('toast.correct_errors_on_page'));

            return;
          }

          await submitForm();
        };

        return (
          <Form autoComplete="off">
            <Card>
              <CardHeader
                title={
                  <Grid container alignItems="center">
                    <Grid item xs={8} sm={9}>
                      <Typography variant="h6">{t('general.profile')}</Typography>

                      <Typography variant="body1">
                        {t('company.company_profile_can_be_created/update_by_manager')}
                      </Typography>
                    </Grid>

                    <Grid container item xs={4} sm={3} justifyContent="flex-end">
                      {canEdit && (
                        <ButtonAction
                          type="button"
                          color="error"
                          disabled={!edit || isUpdatingCompany}
                          onClick={handleCancel}
                        >
                          <Typography variant="button">{t('button.cancel')}</Typography>
                        </ButtonAction>
                      )}
                    </Grid>
                  </Grid>
                }
                sx={{
                  backgroundColor: '#f1f1f1',
                }}
              />

              <Divider />

              <CardContent>
                <Grid container spacing={2}>
                  <Grid container item md={8} xs={12} spacing={1}>
                    <Grid item xs={12}>
                      <TextFieldNormal name="companyname" label={t('contact.company_name')} required disabled={!edit} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="addresslegal" label={t('contact.legal_address')} disabled={!edit} />
                    </Grid>

                    <Grid container item xs={12} spacing={smMediaDown ? 1 : 0}>
                      <Grid item md={3} xs={12}>
                        <Autocomplete
                          name="countrycodelegal"
                          label={t('contact.country_legal')}
                          required
                          disabled={!edit}
                        />
                      </Grid>

                      <Grid item md={3} xs={4}>
                        <TextFieldNormal name="zipcodelegal" label={t('contact.zip_legal')} disabled={!edit} />
                      </Grid>

                      <Grid item md={6} xs={8}>
                        <TextFieldNormal name="citylegal" label={t('contact.city_legal')} disabled={!edit} />
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="addresspost" label={t('contact.post_address')} disabled={!edit} />
                    </Grid>

                    <Grid container item xs={12} spacing={smMediaDown ? 1 : 0}>
                      <Grid item md={3} xs={12}>
                        <Autocomplete
                          name="countrycodepost"
                          label={t('contact.country_post')}
                          required
                          disabled={!edit}
                        />
                      </Grid>

                      <Grid item md={3} xs={4}>
                        <TextFieldNormal name="zipcodepost" label={t('contact.zip_post')} disabled={!edit} />
                      </Grid>

                      <Grid item md={6} xs={8}>
                        <TextFieldNormal name="citypost" label={t('contact.city_post')} disabled={!edit} />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid container item md={4} xs={12} spacing={1}>
                    <Grid item xs={12}>
                      <TextFieldNormal name="fiscalcode" label={t('contact.fiscal_code')} disabled={!edit} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="vatcode" label={t('contact.vat_code')} disabled={!edit} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal
                        name="regcom"
                        label={t('contact.registration_number', {
                          defaultValue: 'Registration number',
                        })}
                        disabled={!edit}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="phone" label={t('general.phone')} disabled={!edit} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="email" label={t('general.email')} disabled={!edit} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextFieldNormal name="skype" label={t('general.skype')} disabled={!edit} />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <TextFieldNormal
                      name="comment"
                      label={t('contact.comments')}
                      multiline
                      maxRows={4}
                      minRows={4}
                      disabled={!edit}
                    />
                  </Grid>
                </Grid>
              </CardContent>

              {canEdit && (
                <>
                  <Divider />

                  <CardActions
                    sx={{
                      mt: 3,
                      mb: 3,
                      mx: 1,
                    }}
                  >
                    <Grid container spacing={smMediaDown ? 1 : 0}>
                      <Grid item sm={8} xs={12}>
                        <ButtonLoading
                          type="button"
                          color="primary"
                          variant="outlined"
                          disabled={edit || isUpdatingCompany}
                          size="small"
                          fullWidth={smMediaDown || undefined}
                          onClick={() => setEdit(true)}
                        >
                          <Typography variant="button">{t('button.edit_profile')}</Typography>
                        </ButtonLoading>
                      </Grid>

                      <Grid item sm={4} xs={12}>
                        <Box display="flex" justifyContent="flex-end">
                          <ButtonLoading
                            type="button"
                            color="primary"
                            variant="outlined"
                            loading={isUpdatingCompany}
                            size="small"
                            fullWidth={smMediaDown || undefined}
                            disabled={!edit || !dirty || isUpdatingCompany}
                            sx={{
                              mr: 0,
                            }}
                            onClick={handleSave}
                          >
                            <Typography variant="button">{t('button.save')}</Typography>
                          </ButtonLoading>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardActions>
                </>
              )}
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};
